import React, { useState } from 'react'
import { useAuth } from '../../context/AuthContext.jsx'

const BASE = import.meta.env.VITE_API_URL || ''

const OAUTH_ERRORS = {
  google_denied:  "You cancelled the Google sign in. Try again when you're ready.",
  invalid_state:  'Something went wrong with the sign in. Please try again.',
  google_failed:  "We couldn't connect to Google right now. Try email sign in instead.",
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
)

export default function AuthPage() {
  const { login, register, requestMagicLink } = useAuth()
  const [mode,    setMode]    = useState('login')
  const [error,   setError]   = useState(() => {
    const params = new URLSearchParams(window.location.search)
    const code   = params.get('error')
    return code ? (OAUTH_ERRORS[code] || 'Something went wrong. Please try again.') : ''
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [devLink, setDevLink] = useState('')

  const [loginEmail,    setLoginEmail]    = useState('')
  const [loginPassword, setLoginPassword] = useState('')

  const [regName,    setRegName]    = useState('')
  const [regEmail,   setRegEmail]   = useState('')
  const [regPass,    setRegPass]    = useState('')
  const [regConfirm, setRegConfirm] = useState('')
  const [regInvite,  setRegInvite]  = useState('')

  const [magicEmail, setMagicEmail] = useState('')

  function switchMode(m) { setMode(m); setError(''); setSuccess(''); setDevLink('') }

  async function handleLogin(e) {
    e.preventDefault(); setError(''); setLoading(true)
    try { await login(loginEmail, loginPassword) }
    catch (err) { setError(err.message) }
    finally { setLoading(false) }
  }

  async function handleRegister(e) {
    e.preventDefault(); setError(''); setLoading(true)
    if (regPass !== regConfirm) { setError('Passwords do not match'); setLoading(false); return }
    try { await register(regName, regEmail, regPass, regInvite || undefined) }
    catch (err) { setError(err.message) }
    finally { setLoading(false) }
  }

  async function handleMagicLink(e) {
    e.preventDefault(); setError(''); setLoading(true)
    try {
      const data = await requestMagicLink(magicEmail)
      setSuccess('If that email is registered, a login link is on its way.')
      if (data.devLink) setDevLink(data.devLink)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const card = {
    background: 'var(--surf)', borderRadius: '16px', padding: '2rem',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)',
  }

  const tab = (id, label) => (
    <button
      key={id}
      onClick={() => switchMode(id)}
      style={{
        flex: 1, padding: '0.65rem 0', background: 'none', border: 'none',
        cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem',
        color: mode === id ? 'var(--accent)' : 'var(--stone)',
        borderBottom: mode === id ? '2px solid var(--accent)' : '2px solid transparent',
        marginBottom: '-1px', fontWeight: mode === id ? 600 : 400, transition: 'color 0.15s',
      }}
    >
      {label}
    </button>
  )

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)', padding: '2rem',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontFamily: 'var(--font-serif)', color: 'var(--ink)', fontSize: '2rem', fontWeight: 400, margin: 0 }}>
            Wellness for Soul
          </h1>
          <p style={{ color: 'var(--stone)', fontSize: '0.88rem', marginTop: '0.3rem' }}>Your personal wellbeing space</p>
        </div>

        <div style={card}>
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(0,0,0,0.08)', marginBottom: '1.5rem' }}>
            {tab('login', 'Sign in')}
            {tab('register', 'Register')}
          </div>

          {error && (
            <div style={{
              background: 'rgba(192,57,43,0.08)', color: '#c0392b', borderRadius: '8px',
              padding: '0.6rem 0.85rem', fontSize: '0.83rem', marginBottom: '1rem',
            }}>
              {error}
            </div>
          )}

          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: '1rem' }}>
                <label className="field-label">Email</label>
                <input className="field-input" type="email" required autoComplete="email"
                  value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                  placeholder="you@example.com" />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label className="field-label">Password</label>
                <input className="field-input" type="password" required autoComplete="current-password"
                  value={loginPassword} onChange={e => setLoginPassword(e.target.value)}
                  placeholder="Your password" />
              </div>
              <button className="save-btn ready" type="submit" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                <button type="button" onClick={() => switchMode('magic')}
                  style={{ background: 'none', border: 'none', color: 'var(--stone)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}>
                  Send me a magic link instead
                </button>
              </div>

              <div className="auth-divider"><span>or</span></div>
              <a href={`${BASE}/api/auth/google/login`} className="btn-google-signin">
                <GoogleIcon />
                Continue with Google
              </a>
            </form>
          )}

          {mode === 'register' && (
            <form onSubmit={handleRegister}>
              <div style={{ marginBottom: '1rem' }}>
                <label className="field-label">Your name</label>
                <input className="field-input" type="text" required
                  value={regName} onChange={e => setRegName(e.target.value)}
                  placeholder="How should we call you?" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label className="field-label">Email</label>
                <input className="field-input" type="email" required autoComplete="email"
                  value={regEmail} onChange={e => setRegEmail(e.target.value)}
                  placeholder="you@example.com" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label className="field-label">Password</label>
                <input className="field-input" type="password" required minLength={8} autoComplete="new-password"
                  value={regPass} onChange={e => setRegPass(e.target.value)}
                  placeholder="At least 8 characters" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label className="field-label">Confirm password</label>
                <input className="field-input" type="password" required autoComplete="new-password"
                  value={regConfirm} onChange={e => setRegConfirm(e.target.value)}
                  placeholder="Repeat your password" />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label className="field-label">
                  Invitation code{' '}
                  <span style={{ color: 'var(--stone)', fontWeight: 400 }}>(required unless you're the first user)</span>
                </label>
                <input className="field-input" type="text"
                  value={regInvite} onChange={e => setRegInvite(e.target.value)}
                  placeholder="Leave empty if registering first" />
              </div>
              <button className="save-btn ready" type="submit" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Creating account…' : 'Create account'}
              </button>
            </form>
          )}

          {mode === 'magic' && (
            <div>
              {success ? (
                <>
                  <p style={{ color: 'var(--bark)', fontSize: '0.88rem', lineHeight: 1.65, marginBottom: '1rem' }}>
                    {success}
                  </p>
                  {devLink && (
                    <div style={{ background: 'rgba(0,0,0,0.04)', borderRadius: '8px', padding: '0.75rem', fontSize: '0.78rem', marginBottom: '1rem' }}>
                      <p style={{ color: 'var(--stone)', marginBottom: '0.35rem', fontWeight: 600 }}>DEV — click to sign in:</p>
                      <a href={devLink} style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{devLink}</a>
                    </div>
                  )}
                  <button className="ghost-btn" style={{ width: '100%' }} onClick={() => switchMode('login')}>
                    Back to sign in
                  </button>
                </>
              ) : (
                <form onSubmit={handleMagicLink}>
                  <p style={{ color: 'var(--stone)', fontSize: '0.83rem', marginBottom: '1.25rem', lineHeight: 1.65 }}>
                    Enter your email and we'll send a one-time login link. No password needed.
                  </p>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label className="field-label">Email</label>
                    <input className="field-input" type="email" required autoFocus
                      value={magicEmail} onChange={e => setMagicEmail(e.target.value)}
                      placeholder="you@example.com" />
                  </div>
                  <button className="save-btn ready" type="submit" style={{ width: '100%' }} disabled={loading}>
                    {loading ? 'Sending…' : 'Send magic link'}
                  </button>
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <button type="button" onClick={() => switchMode('login')}
                      style={{ background: 'none', border: 'none', color: 'var(--stone)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}>
                      Back to sign in
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
